import { z } from 'zod';

// ── Frontend UI models ──────────────────────────────────────

export const DashboardStats = z.object({
  activeContracts: z.number(),
  totalDevicesEnrolled: z.number(),
  revenueThisMonth: z.number(),
  outstandingBalance: z.number(),
  overduePaymentsCount: z.number(),
  totalCustomers: z.number(),
  collectionRate: z.number(),
  pendingApplications: z.number(),
});
export type DashboardStatsModel = z.infer<typeof DashboardStats>;

export const RecentPayment = z.object({
  id: z.string(),
  contractNumber: z.string(),
  customerName: z.string(),
  amount: z.number(),
  paymentMethod: z.string(),
  createdAt: z.string(),
});
export type RecentPaymentModel = z.infer<typeof RecentPayment>;

export const OverdueContract = z.object({
  id: z.string(),
  contractNumber: z.string(),
  customerName: z.string(),
  overdueAmount: z.number(),
  daysPastDue: z.number(),
  nextPaymentDate: z.string(),
});
export type OverdueContractModel = z.infer<typeof OverdueContract>;

export const DashboardPeriodSchema = z.enum(['today', 'week', 'month']);
export type DashboardPeriod = z.infer<typeof DashboardPeriodSchema>;
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 'week';

export const ActivityPointSchema = z.object({
  bucketStart: z.string(),
  /** Null for a bucket that has not started yet. */
  collected: z.number().nullable(),
  /** Null for hourly buckets — installments fall due per day. */
  expected: z.number().nullable(),
});
export type ActivityPoint = z.infer<typeof ActivityPointSchema>;

export const DashboardActivitySchema = z.object({
  period: DashboardPeriodSchema,
  granularity: z.enum(['hour', 'day']),
  timeZone: z.string(),
  windowStart: z.string(),
  windowEnd: z.string(),
  collected: z.number(),
  installmentCollected: z.number(),
  expected: z.number(),
  collectionRate: z.number().nullable(),
  previousCollected: z.number(),
  newContracts: z.number(),
  series: z.array(ActivityPointSchema),
});
export type DashboardActivity = z.infer<typeof DashboardActivitySchema>;

// ── Backend API response types (camelCase) ─────────────────

export interface BackendDashboardStats {
  totalContracts: number;
  activeContracts: number;
  completedContracts: number;
  defaultedContracts: number;
  totalCustomers: number;
  totalDevices: number;
  totalRevenue: number;
  outstandingBalance: number;
  pendingApplications: number;
}

export interface BackendOverdueContract {
  contractId: string;
  customerName: string;
  customerPhone: string;
  deviceInfo: string;
  /** Device unit id — null when the contract has no unit attached. Needed to
   *  lock a device straight from a collections row. */
  deviceId: string | null;
  deviceLocked: boolean;
  overdueAmount: number;
  daysOverdue: number;
  dueDate: string;
}
